import React, { ReactNode, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faFloppyDisk } from "@fortawesome/pro-solid-svg-icons";
import { faCheck } from "@fortawesome/pro-solid-svg-icons/faCheck";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { faPencil } from "@fortawesome/pro-solid-svg-icons/faPencil";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MenuProps } from "antd/es/menu";
import notification from "antd/es/notification";
import Paragraph from "antd/es/typography/Paragraph";
import { useIntl } from "react-intl";

import Draft from "@mapmycustomers/shared/types/draft/Draft";
import EmailDraft from "@mapmycustomers/shared/types/draft/EmailDraft";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";
import { Dropdown } from "@mapmycustomers/ui";

import EmailDraftsModal from "@app/component/email/creation/EmailDraftsModal";
import EmailSubjectView from "@app/component/email/creation/EmailSubjectView";
import { areEmailDraftsLoaded } from "@app/store/email";
import { RootState } from "@app/store/rootReducer";
import EmailEntityOpenCallback from "@app/types/email/EmailEntityOpenCallback";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useEmailDraftInfoGetter from "@app/util/email/useEmailDraftInfoGetter";
import useEmailDraftSave from "@app/util/email/useEmailDraftSave";
import useEntityTypeEmailDrafts from "@app/util/email/useEntityTypeEmailDrafts";

import EmailContext from "../EmailContext";

import styles from "./EmailDraftsButton.module.scss";
import messages from "./messages";

interface EmailDraftsButtonProps {
  onEntityOpen: EmailEntityOpenCallback;
}

const EmailDraftsButton = ({
  draftsLoaded,
  onEntityOpen,
}: EmailDraftsButtonProps & ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();
  const analytics = useExtendedAnalytics("Drafts Menu", useAnalytics());

  const { draftId, draftSaving, entityType, onDraftChange } = useNonNullableContext(EmailContext);

  const saveDraft = useEmailDraftSave();
  const draftInfoGetter = useEmailDraftInfoGetter();

  const [draftsModalVisible, showDraftsModal, hideDraftsModal] = useBoolean();
  const [draftsModalInitialSelectedDraftId, setDraftsModalInitialSelectedDraftId] = useState<
    EmailDraft["id"] | undefined
  >();

  const drafts = useEntityTypeEmailDrafts(entityType);

  const handleDraftClick = useDynamicCallback((draft: Draft) => {
    const { valid } = draftInfoGetter(draft.id, intl)!;

    if (valid) {
      onDraftChange(draft.id);
    } else {
      setDraftsModalInitialSelectedDraftId(draft.id);
      showDraftsModal();
    }

    analytics.clicked(["Select Draft"]);
  });

  const handleMoreDraftsClick = useDynamicCallback(() => {
    setDraftsModalInitialSelectedDraftId(draftId);
    showDraftsModal();
    analytics.initiated(["More Drafts"]);
  });

  const handleSaveDraftClick = useDynamicCallback(() => {
    saveDraft({
      onFailure: () => {
        notification.error({
          description: intl.formatMessage(messages.draftSaveErrorDescription),
          message: intl.formatMessage(messages.draftSaveErrorMessage),
        });
      },
      onSuccess: () => {
        notification.success({
          message: intl.formatMessage(messages.draftSaveSuccessMessage),
        });
        analytics.completed(["Save"]);
      },
    });
  });

  const handleDraftApply = useDynamicCallback((draftId: Draft["id"]) => {
    onDraftChange(draftId);
    hideDraftsModal();
  });

  const menuItems: MenuProps["items"] = useMemo(() => {
    const getMenuItem = (text: ReactNode, icon?: React.ReactNode) => (
      <span className={styles.menuItem}>
        <span className={styles.menuItemIcon}>{icon}</span>
        <Paragraph
          className={styles.menuItemParagraph}
          ellipsis={{
            expandable: false,
            rows: 1,
            tooltip: {
              placement: "left",
            },
          }}
        >
          {text}
        </Paragraph>
      </span>
    );

    return [
      ...(drafts.length > 0
        ? [
            {
              className: styles.headerMenuItem,
              key: "header",
              label: intl.formatMessage(messages.mostRecent),
            },
            ...drafts.map((draft) => {
              const { subjectDynamicVariables } = draftInfoGetter(draft.id, intl)!;

              return {
                className: draft.id === draftId ? styles.selected : undefined,
                key: `draft-${draft.id}`,
                label: getMenuItem(
                  draft.title ? (
                    <EmailSubjectView
                      dynamicVariables={subjectDynamicVariables}
                      subject={draft.title}
                    />
                  ) : (
                    intl.formatMessage(messages.noSubject)
                  ),
                  draft.id === draftId ? <FontAwesomeIcon icon={faCheck} /> : undefined
                ),
                onClick: () => handleDraftClick(draft),
              };
            }),
            {
              key: "seeMoreDivider",
              type: "divider",
            },
            {
              key: "seeMore",
              label: (
                <div className={styles.seeMoreContainer}>
                  <span>{getMenuItem(intl.formatMessage(messages.seeMore))}</span>
                  <FontAwesomeIcon icon={faChevronRight} />
                </div>
              ),
              onClick: handleMoreDraftsClick,
            },
            {
              key: "draftsDivider",
              type: "divider",
            },
          ]
        : []),
      ...[
        {
          disabled: draftSaving,
          key: "save",
          label: getMenuItem(
            intl.formatMessage(messages.saveAsDraft),
            <FontAwesomeIcon icon={faFloppyDisk} />
          ),
          onClick: handleSaveDraftClick,
        },
      ],
    ];
  }, [
    intl,
    handleMoreDraftsClick,
    draftSaving,
    handleSaveDraftClick,
    drafts,
    draftInfoGetter,
    draftId,
    handleDraftClick,
  ]);

  if (!draftsLoaded) {
    return null;
  }

  return (
    <AnalyticsContext.Provider value={analytics}>
      <Dropdown
        disabled={!draftsLoaded}
        menu={{ items: menuItems }}
        overlayClassName={styles.dropdown}
      >
        <div className={styles.buttonContainer}>
          <div className={styles.button}>
            <FontAwesomeIcon icon={faPencil} />
            <span>{intl.formatMessage(messages.drafts)}</span>
            <FontAwesomeIcon icon={faChevronDown} />
          </div>
        </div>
      </Dropdown>
      {draftsModalVisible && (
        <EmailDraftsModal
          entityType={entityType}
          initialSelectedDraftId={draftsModalInitialSelectedDraftId}
          onApply={handleDraftApply}
          onEntityOpen={onEntityOpen}
          onHide={hideDraftsModal}
        />
      )}
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  draftsLoaded: areEmailDraftsLoaded(state),
});

export default connect(mapStateToProps)(EmailDraftsButton);
